import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useVirtualizer } from '@tanstack/react-virtual';
import { monitorForElements } from "@atlaskit/pragmatic-drag-and-drop/element/adapter";
import { autoScrollForElements, autoScrollWindowForElements } from '@atlaskit/pragmatic-drag-and-drop-auto-scroll/element';
import { showSuccess, showError } from '../../utils/snackbarHelpers';
import { showSnackBar } from '../../redux/Slices/snackbarSlice';
import { fetchUsers } from '../../redux/Slices/thunks';
import { config } from '../../config';
import { useBoard } from '../../context/BoardContext';
import { debounce, find, getFilters, updateCardDetails, initialFiltersState } from '../../utils/helpers';
import { selectBoardId, setBoardId } from '../../redux/Slices/boardIdSlice';
import { fetchWorkspaces } from '../../redux/Slices/workspaceSlice';
import { setFilterQuery } from '../../redux/Slices/filterQuerySlice';
import ActionListPND from './ActionListPND';
import AddActionListButton from './AddActionListButton';
import Navbar from './Navbar';
import NotFound from '../400';
import AccessDenied from '../AccessDenied';
import useIsScrolling from '../../hooks/useIsScrolling';
import SkeletonBoard from '../Skeletons/SkeletonBoard';
import Close from '../icons/Close';
import {
  fetchBoardById,
  resetBoard,
  setLoading,
  optimisticUpdateBoard,
  optimisticAddList,
  optimisticUpdateList,
  optimisticDeleteList,
  optimisticReorderLists,
} from '../../redux/Slices/boardSlice';
import { Helmet } from 'react-helmet';
import { fetchMembers } from '../../redux/Slices/memberSlice';
import {
  fetchCardsByListId,
  updateTotalCardsOnMove,
  incrementTotalCards,
  decrementTotalCards
} from '../../redux/Slices/actionlistSlice';
import { fetchBoardLists } from '../../redux/Slices/boardsSlice';
import MetaTags from '../Global/MetaTags';
import { useWhiteLabel } from '../../context/WhiteLabelContext';
import { fetchCardById } from '../../redux/Slices/cardSlice';
import { ActionTypes } from '../../sockets/ActionTypes';
import { setCardMoveTarget } from '../../redux/Slices/moveCardSlice';
import useThemeAwareColors from '../../hooks/useThemeAwareColors';
import { useSocketConnection, useSocketAction } from '../../hooks/useSocketManager';

// Import optimized scrolling styles
import './scss/optimized-scrolling.css';
import PaymentNotice from '../Billing/PaymentNotice';
import CardRecommendationSnackbar from '../AI/CardRecommendationSnackbar';
import AICardSuggestions from '../AI/AICardSuggestions';
import recommendationNotificationService from '../../utils/recommendationNotificationService';
import useBoardPermissions from '../../hooks/useBoardPermissions';



const Board = ({ board, setBoard }) => {
  const { boardId, boardSlug, cardId } = useParams();
  const { setBoardPermalink } = useBoard();
  const _boardId = useSelector(selectBoardId);

  const dispatch = useDispatch();
  const location = useLocation();


  const [showForm, setShowForm] = useState(false);
  const [listTitle, setActionListTitle] = useState('');
  const [isValid, setIsValid] = useState(true);
  const [openListId, setOpenListId] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [fetchedLists, setFetchedLists] = useState(new Set());
  const [highlight, setHighlight] = useState(false);
  const [cardCount, setCardCount] = useState(0);
  const [navbarHeight, setNavbarHeight] = useState(0);
  const [hasLoadedInitialCards, setHasLoadedInitialCards] = useState(false);
  const [accessError, setAccessError] = useState(null);

  // Card recommendation states
  const [showRecommendationSnackbar, setShowRecommendationSnackbar] = useState(false);
  const [showRecommendationModal, setShowRecommendationModal] = useState(false);

  const navbarRef = useRef(null);
  const paymentNoticeRef = useRef(null);

  // Enable automatic theme-aware color mapping for lists
  useThemeAwareColors(board, setBoard);

  // Get board permissions
  const boardPermissions = useBoardPermissions(board);

  const { actionLists } = useSelector(state => state.boards);
  const { user } = useSelector((state) => state.user);

  const { getAppName } = useWhiteLabel();
  const appName = getAppName();

  const textareaRef = useRef(null);
  const parentRef = useRef(null);
  const boardRef = useRef(null);
  const formRef = useRef(null);
  const isScrolling = useIsScrolling(parentRef);

  const { boardLoading } = useSelector((state) => state.board);
  const filterQuery = useSelector((state) => state.filterQuery);

  const fetchedListsRef = useRef(new Set());
  const prevFilteredStatusRef = useRef(filterQuery.filteredStatus);
  const inFlightFetchesRef = useRef(new Map()); // Track in-flight card fetches to prevent race conditions


  // Monitor Drag and Drop
  useEffect(() => {
    const element = boardRef.current;
    const monitorConfig = {
      element,
      onDrag({ location }) {
        const target = location.current.dropTargets[0];
        setHighlight(target && target.data.boardShortId === _boardId);
      },
      onDrop() {
        setHighlight(false);
      },
    };

    return monitorForElements(monitorConfig);
  }, [_boardId]);

  useEffect(() => {

    const updateNavbarHeight = () => {

      if (navbarRef.current) {
        const navbarHeight = navbarRef.current.offsetHeight;
        const paymentNoticeHeight = paymentNoticeRef.current?.offsetHeight || 0;
        const totalHeight = navbarHeight + paymentNoticeHeight;
        setNavbarHeight(totalHeight);
      }
    };
    updateNavbarHeight(); // Initial calculation
    window.addEventListener('resize', updateNavbarHeight);
    return () => {
      window.removeEventListener('resize', updateNavbarHeight);
    };
  }, [navbarRef.current, paymentNoticeRef.current]);

  // Update navbar height when PaymentNotice visibility changes
  useEffect(() => {
    const updateNavbarHeight = () => {
      if (navbarRef.current) {
        const navbarHeight = navbarRef.current.offsetHeight;
        const paymentNoticeHeight = paymentNoticeRef.current?.offsetHeight || 0;
        const totalHeight = navbarHeight + paymentNoticeHeight;
        setNavbarHeight(totalHeight);
      }
    };

    // Use a small delay to ensure DOM has updated after PaymentNotice renders/hides
    const timeoutId = setTimeout(updateNavbarHeight, 100);

    return () => clearTimeout(timeoutId);
  }, [board?.wShortId]); // Re-run when workspace changes (which could affect PaymentNotice visibility)

  // Watch for PaymentNotice DOM changes (visibility, size)
  useEffect(() => {
    if (!paymentNoticeRef.current) return;

    const updateNavbarHeight = () => {
      if (navbarRef.current) {
        const navbarHeight = navbarRef.current.offsetHeight;
        const paymentNoticeHeight = paymentNoticeRef.current?.offsetHeight || 0;
        const totalHeight = navbarHeight + paymentNoticeHeight;
        setNavbarHeight(totalHeight);
      }
    };

    // Create a MutationObserver to watch for changes in PaymentNotice
    const observer = new MutationObserver(() => {
      updateNavbarHeight();
    });

    // Create a ResizeObserver to watch for size changes
    const resizeObserver = new ResizeObserver(() => {
      updateNavbarHeight();
    });

    // Observe the PaymentNotice container for changes
    observer.observe(paymentNoticeRef.current, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['style', 'class']
    });

    resizeObserver.observe(paymentNoticeRef.current);

    return () => {
      observer.disconnect();
      resizeObserver.disconnect();
    };
  }, [paymentNoticeRef.current]);


  // Virtualizer configuration with optimized settings for board scrolling
  const virtualizer = useVirtualizer({
    horizontal: true,
    count: (board?.actionLists?.length || 0) + 1,
    getScrollElement: () => parentRef.current || boardRef.current,
    estimateSize: index => {
      // Safely access board properties with fallbacks
      if (board?.actionLists && index < board.actionLists.length) {
        return board.actionLists[index]?.customWidth || 290;
      }
      return 290; // Default width
    },
  });

  // Fetch filters on component mount
  useEffect(() => {
    const fetchFilters = async () => {
      try {
        const response = await getFilters({ boardId });

        // Validate response properties before destructuring
        if (response && response.filters && response.cardCount !== undefined) {
          const { filters, cardCount, labels } = response;
          dispatch(setFilterQuery({ ...filters, labels: labels || [] }));
          setCardCount(cardCount);
        } else {
          console.error('Invalid response:', response);
        }
      } catch (error) {
        console.error('Error fetching filters:', error);
      }
    };

    fetchFilters();
  }, [boardId, dispatch]);

  // Function to clear filters - defined outside useEffect to avoid recreating
  const clearFilters = useCallback(async () => {
    dispatch(setFilterQuery(initialFiltersState));
    // Also update the backend
    try {
      const response = await fetch(`${config.API_URI}/v1/users/me/filters/update-only`, {
        method: 'PUT',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ filters: initialFiltersState, boardId })
      });
      if (!response.ok) {
        throw new Error('Failed to update filters');
      }

      const data = await response.json();
      setFilterQuery(data.filters);
      setCardCount(data.cardCount);
    } catch (error) {
      console.error('Error clearing filters:', error);
    }
  }, [dispatch, boardId, setFilterQuery, setCardCount]);

  // Listen for clear filters event from snackbar
  useEffect(() => {
    const handleClearFiltersEvent = () => {
      clearFilters();
    };

    window.addEventListener('clearFilters', handleClearFiltersEvent);
    return () => {
      window.removeEventListener('clearFilters', handleClearFiltersEvent);
    };
  }, [clearFilters]);

  // Show filter status snackbar when visiting board with active filters
  useEffect(() => {
    if (filterQuery && filterQuery.filteredStatus) {
      // Show snackbar with clear filters action only visit this board and just once
      if (location.pathname.includes(`/b/${_boardId}`)) {
        dispatch(showSnackBar({
          message: 'Filters are currently active',
          type: 'info',
          persistent: false,
          undoAction: 'CLEAR_FILTERS', // Use string identifier instead of function
          undoLabel: 'Clear Filters'
        }));
      }
    }
  }, [filterQuery?.filteredStatus, _boardId, dispatch]);


  const prevPath = useRef(location.pathname);
  const isNavigatingFromCardRef = useRef(false);
  const navigationTimeoutRef = useRef(null);
  const lastNavigationTime = useRef(0);
  const isDirectCardVisit = useRef(false);
  // Initialize allowFetching - allow fetching by default, only disable during navigation
  const allowFetchingRef = useRef(true);

  useEffect(() => {
    const fetchBoardData = async () => {
      if (!boardId && !_boardId) return;

      setIsLoading(true);
      setAccessError(null);
      dispatch(setLoading(true));

      try {
        // Reset board data before fetching
        setBoard({ actionLists: [] });
        setFetchedLists(new Set());

        const boardData = await dispatch(fetchBoardById(boardId || _boardId)).unwrap();

        setBoard((prevBoard) => ({
          ...boardData,
          actionLists: boardData.actionLists.map((list) => ({
            ...list,
            cards: prevBoard?.actionLists?.find((prevList) => prevList.id === list.id)?.cards || [],
          })),
        }));
      } catch (error) {
        console.error("Error fetching the board:", error);

        // Handle access denied errors
        if (error?.type === 'ACCESS_DENIED' || error?.status === 403) {
          // Check if this is a workspace binding error
          const errorData = error?.data || {};
          const isWorkspaceBindingError = errorData.workspaceBinding === true;

          let errorMessage = error?.message || 'You do not have access to this board';

          // Add hint for workspace binding errors
          if (isWorkspaceBindingError && errorData.hint) {
            errorMessage = `${errorMessage}\n\n${errorData.hint}`;
            if (errorData.boundDomain) {
              errorMessage = `${errorMessage}\n\nBound Domain: ${errorData.boundDomain}`;
            }
          }

          setAccessError({
            type: 'ACCESS_DENIED',
            message: errorMessage,
            isWorkspaceBinding: isWorkspaceBindingError,
            boundDomain: errorData.boundDomain
          });
        } else if (error?.type === 'NOT_FOUND' || error?.status === 404) {
          setAccessError({
            type: 'NOT_FOUND',
            message: error?.message || 'Board not found'
          });
        } else {
          setAccessError({
            type: 'ERROR',
            message: error?.message || 'Failed to load board'
          });
        }
      } finally {
        setIsLoading(false);
        dispatch(setLoading(false));
      }
    };

    // Detect navigation patterns
    const isNavigatingBetweenBAndC =
      (prevPath.current?.split("/")[1] === "b" && location.pathname.split("/")[1] === "c") ||
      (prevPath.current?.split("/")[1] === "c" && location.pathname.split("/")[1] === "b");

    const isNavigatingFromCard = prevPath.current?.split("/")[1] === "c" && location.pathname.split("/")[1] === "b";
    const isNavigatingToCard = prevPath.current?.split("/")[1] === "b" && location.pathname.split("/")[1] === "c";

    // Check if this is a direct visit to a card (no previous path or previous path was not from this app)
    const isDirectVisit = !prevPath.current || prevPath.current === location.pathname;
    isDirectCardVisit.current = isDirectVisit && location.pathname.split("/")[1] === "c";

    // Update the ref to track card navigation
    isNavigatingFromCardRef.current = isNavigatingFromCard;

    // Only control fetching for actual navigation, not direct visits
    if ((isNavigatingFromCard || isNavigatingToCard) && !isDirectVisit) {
      allowFetchingRef.current = false;
      lastNavigationTime.current = Date.now();

      // Clear any existing timeout
      if (navigationTimeoutRef.current) {
        clearTimeout(navigationTimeoutRef.current);
        navigationTimeoutRef.current = null;
      }

      // Reset the navigation flag after a delay to allow normal fetching to resume
      navigationTimeoutRef.current = setTimeout(() => {
        console.log('[Board] Re-enabling card fetching after navigation delay');
        isNavigatingFromCardRef.current = false;
        allowFetchingRef.current = true;
        navigationTimeoutRef.current = null;
      }, 500); // Reduced to 500ms for faster recovery
    } else if (!isNavigatingBetweenBAndC && location.pathname.split("/")[1] === "b") {
      // If we're on the board page and not navigating between b and c, ensure fetching is enabled
      // This handles the case where the timeout might have been cleared or never set
      if (!allowFetchingRef.current && Date.now() - lastNavigationTime.current > 500) {
        console.log('[Board] Force re-enabling card fetching (fallback)');
        allowFetchingRef.current = true;
        isNavigatingFromCardRef.current = false;
      }
    }

    if (!isNavigatingBetweenBAndC) {
      fetchBoardData();
    }

    // Store the current path as previous for next render
    prevPath.current = location.pathname;
  }, [dispatch, boardId, _boardId, location.pathname]);

  // Separate effect to handle timeout cleanup on unmount only
  useEffect(() => {
    return () => {
      if (navigationTimeoutRef.current) {
        clearTimeout(navigationTimeoutRef.current);
        navigationTimeoutRef.current = null;
      }
    };
  }, []);

  // Check for card recommendations when board loads
  useEffect(() => {
    const checkRecommendations = async () => {
      if (!board?.shortId || isLoading) return;

      // Check if we should show recommendation notification
      const shouldShow = recommendationNotificationService.shouldShowNotification(board.shortId);

      if (shouldShow) {
        // Delay showing the snackbar to let the board load
        setTimeout(() => {
          setShowRecommendationSnackbar(true);
          recommendationNotificationService.markNotificationShown(board.shortId);
        }, 2000); // 2 second delay
      } else {
        // Just mark the visit
        recommendationNotificationService.markBoardVisit(board.shortId);
      }
    };

    checkRecommendations();
  }, [board?.shortId, isLoading]);


  // Memoize the fetchCardsForList function to prevent unnecessary re-renders
  const fetchCardsForList = useCallback(async (listId, isFilter = false) => {
    if (!listId) return;

    // Additional check to prevent fetching during navigation
    if (!allowFetchingRef.current) {
      console.log('[Board] Skipping card fetch - fetching disabled');
      return;
    }

    // Allow fetching if this is a direct card visit, otherwise check navigation timing
    const isOnCardPage = location.pathname.split("/")[1] === "c";
    if (isOnCardPage && !isDirectCardVisit.current) {
      console.log('[Board] Skipping card fetch - on card page');
      return;
    }

    // Prevent duplicate fetches for the same list (race condition protection)
    if (inFlightFetchesRef.current.has(listId)) {
      console.log(`[Board] Skipping duplicate fetch for list ${listId} - already in flight`);
      return;
    }

    // Mark this list as being fetched
    inFlightFetchesRef.current.set(listId, Date.now());

    try {
      const result = await dispatch(fetchCardsByListId({ actionListId: listId, skip: 0, limit: 10, filterQuery })).unwrap();
      const cards = result?.cards || [];

      // Validate that all returned cards belong to the requested list
      const invalidCards = cards.filter(card => {
        const cardListId = card.actionList?._id || card.actionList;
        return cardListId && cardListId !== listId;
      });

      if (invalidCards.length > 0) {
        console.error(`[Board] Received ${invalidCards.length} cards that don't belong to list ${listId}:`, invalidCards);
        // Filter out invalid cards to prevent cross-contamination
        const validCards = cards.filter(card => {
          const cardListId = card.actionList?._id || card.actionList;
          return !cardListId || cardListId === listId;
        });
        console.log(`[Board] Filtered to ${validCards.length} valid cards for list ${listId}`);
        setHasLoadedInitialCards(true);

        setBoard((prevBoard) => {
          if (!prevBoard || !prevBoard.actionLists) return prevBoard;

          // Verify the list still exists in the board before updating
          const listExists = prevBoard.actionLists.some(list => list._id === listId);
          if (!listExists) {
            console.warn(`[Board] List ${listId} not found in board, skipping card update`);
            return prevBoard;
          }

          const updatedLists = prevBoard.actionLists.map((list) => {
            // Strict ID matching to prevent cross-contamination
            if (list._id === listId) {
              // If filtering, always replace cards even if empty
              // If not filtering, replace cards only if fetched cards exist; else clear cards (set [])
              if (isFilter) {
                return { ...list, cards: validCards };
              } else {
                return { ...list, cards: validCards.length > 0 ? validCards : [] };
              }
            }
            return list;
          });

          return { ...prevBoard, actionLists: updatedLists };
        });
        return;
      }

      setHasLoadedInitialCards(true);

      setBoard((prevBoard) => {
        if (!prevBoard || !prevBoard.actionLists) return prevBoard;

        // Verify the list still exists in the board before updating
        const listExists = prevBoard.actionLists.some(list => list._id === listId);
        if (!listExists) {
          console.warn(`[Board] List ${listId} not found in board, skipping card update`);
          return prevBoard;
        }

        const updatedLists = prevBoard.actionLists.map((list) => {
          // Strict ID matching to prevent cross-contamination
          if (list._id === listId) {
            // If filtering, always replace cards even if empty
            // If not filtering, replace cards only if fetched cards exist; else clear cards (set [])
            if (isFilter) {
              return { ...list, cards };
            } else {
              return { ...list, cards: cards.length > 0 ? cards : [] };
            }
          }
          return list;
        });

        return { ...prevBoard, actionLists: updatedLists };
      });
    } catch (error) {
      console.error(`Error fetching cards for list ${listId}:`, error);
    } finally {
      // Remove from in-flight fetches
      inFlightFetchesRef.current.delete(listId);
    }
  }, [dispatch, filterQuery, location.pathname]);

  // Effect to refetch cards when filter changes
  useEffect(() => {
    if (!board?.actionLists) return;

    // Don't fetch cards if fetching is disabled
    if (!allowFetchingRef.current) {
      return;
    }

    const visibleIndexes = virtualizer.getVirtualItems().map(v => v.index);
    const visibleListIds = visibleIndexes
      .map(i => board?.actionLists?.[i]?._id)
      .filter(Boolean);

    // Create a filter signature to detect any filter changes
    const currentFilterSignature = JSON.stringify(filterQuery);

    // Clear fetchedListsRef if any filter changed, but not when fetching is disabled
    if (currentFilterSignature !== prevFilteredStatusRef.current && allowFetchingRef.current) {
      fetchedListsRef.current.clear();
      // Also clear in-flight fetches to allow re-fetching with new filters
      inFlightFetchesRef.current.clear();
    }

    visibleListIds.forEach((listId) => {
      if (!fetchedListsRef.current.has(listId)) {
        fetchedListsRef.current.add(listId);
        fetchCardsForList(listId, true);
      }
    });

    prevFilteredStatusRef.current = currentFilterSignature;
  }, [filterQuery, board?.actionLists, virtualizer.getVirtualItems()]);




  useEffect(() => {
    if (boardId && _boardId !== boardId) {
      dispatch(setBoardId(boardId));
    }

    // Only clear fetchedListsRef if fetching is allowed
    if (boardLoading && allowFetchingRef.current) {
      fetchedListsRef.current.clear();
      // Also clear in-flight fetches when board is loading
      inFlightFetchesRef.current.clear();
    }

  }, [boardId, _boardId, dispatch, boardLoading]);

  useEffect(() => {
    if (_boardId && !actionLists[_boardId]) {
      // Fetch board lists without cards for better performance
      dispatch(fetchBoardLists({ boardId: _boardId, includeCards: false }));
    }
  }, [_boardId]);


  useEffect(() => {
    if (!board?.wShortId) return;
    dispatch(fetchMembers({ type: 'workspace', id: board.wShortId }));
  }, [dispatch, board?.wShortId]);

  // Fetch board members for filtering
  useEffect(() => {
    if (!_boardId) return;
    dispatch(fetchMembers({ type: 'board', id: _boardId }));
  }, [dispatch, _boardId]);


  // Handle adding new action list
  const handleAddList = async (e) => {
    e.preventDefault();

    const newList = { id: Date.now(), title: listTitle }; // Temporary list with a unique id

    // Optimistically update the state
    setBoard((prev) => ({
      ...prev,
      actionLists: [...prev.actionLists, newList],
    }));

    setActionListTitle('');
    setShowForm(true); // Reopen form
    setTimeout(() => {
      textareaRef.current?.focus();
      formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' }); // Scroll into view again
    }, 0);

    try {

      const response = await fetch(`${config.API_URI}/v1/actionLists`, {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ boardId: _boardId, title: listTitle }),
      });

      if (!response.ok) throw new Error('Failed to add action list');

      const data = await response.json();
      const addedList = data.actionList;

      // Replace the temporary list with the one from the server
      setBoard((prev) => ({
        ...prev,
        actionLists: prev.actionLists.map((list) =>
          list.id === newList.id ? addedList : list
        ),
      }));


      showSuccess(dispatch, 'Action list added successfully!');
    } catch (error) {

      // Revert the optimistic update
      setBoard((prev) => ({
        ...prev,
        actionLists: prev.actionLists.filter((list) => list.id !== newList.id),
      }));

      showError(dispatch, 'Error adding action list');
    }
  };

  // Handle textarea height adjustment
  useEffect(() => {
    if (textareaRef.current) {
      const resetHeight = () => {
        textareaRef.current.style.height = '20px';
        const scrollHeight = textareaRef.current.scrollHeight - 20;
        textareaRef.current.style.height = `${scrollHeight}px`;
      };

      resetHeight();
    }
  }, [listTitle]);


  useEffect(() => {
    if (!parentRef.current) return; // Wait until it exists
    const autoScroll = autoScrollForElements({
      element: parentRef.current,
      getConfiguration: () => ({
        maxScrollSpeed: 'fast',
        allowedAxis: 'all',
      }),
    });
    return () => { autoScroll(); };
  }, [parentRef.current]);




  // Initialize socket connection (app-wide)
  useSocketConnection();

  // Real-time event handlers for this board
  useSocketAction([
    ActionTypes.ACTIONLIST_CREATED,
    ActionTypes.ACTIONLIST_UPDATED,
    ActionTypes.ACTIONLIST_DELETED,
    ActionTypes.LIST_MOVED,
    ActionTypes.CARD_CREATED,
    ActionTypes.CARD_UPDATED,
    ActionTypes.CARD_DELETED,
    ActionTypes.CARD_MOVED,
    ActionTypes.CARD_ARCHIVED,
    ActionTypes.CARD_UNARCHIVED,
    ActionTypes.BOARD_UPDATED,
    ActionTypes.BOARD_DELETED,
  ], (data) => {
    // Only process events for the current board
    if (data.boardId !== boardId) return;

    // Skip events triggered by current user (already handled optimistically)
    if (data.reqUser === user?.user?._id) return;

    console.log('[Board] Real-time event:', data.action, data);

    switch (data.action) {
      case ActionTypes.ACTIONLIST_CREATED:
        // ✅ Add new list using optimistic action (no API call)
        if (data.actionList) {
          dispatch(optimisticAddList(data.actionList));
        }
        break;

      case ActionTypes.ACTIONLIST_UPDATED:
        // ✅ Update list properties using optimistic action (no API call)
        if (data.actionList && data.updatedFields) {
          dispatch(optimisticUpdateList({
            listId: data.actionList._id,
            updates: data.updatedFields
          }));
        }
        break;

      case ActionTypes.ACTIONLIST_DELETED:
        // ✅ Remove list using optimistic action (no API call)
        dispatch(optimisticDeleteList(data.actionListId));
        break;

      case ActionTypes.LIST_MOVED:
        // ✅ Update context directly to preserve all cards
        // Don't use Redux for this as it may not have all cards loaded
        if (data.actionListOrders) {
          setBoard((prevBoard) => {
            if (!prevBoard || !prevBoard.actionLists) return prevBoard;

            const orderLookup = new Map(data.actionListOrders.map(({ _id, order }) => [_id, order]));

            // Create a new sorted array preserving all list data including cards
            const sortedLists = [...prevBoard.actionLists].sort((a, b) => {
              const orderA = orderLookup.get(a._id) ?? a.order ?? 0;
              const orderB = orderLookup.get(b._id) ?? b.order ?? 0;
              return orderA - orderB;
            });

            return { ...prevBoard, actionLists: sortedLists };
          });
        }
        break;

      case ActionTypes.CARD_CREATED:
        // ✅ Add new card to context
        if (data.card && data.actionListId) {
          setBoard((prevBoard) => {

            if (!prevBoard?.actionLists) return prevBoard;

            return {
              ...prevBoard,
              cardsCount: (prevBoard.cardsCount || 0) + 1,
              actionLists: prevBoard.actionLists.map((list) => {
                if (list._id === data.actionListId || list.shortId === data.actionListId) {
                  const cards = list.cards || [];

                  // Check if card already exists
                  const cardExists = cards.some(c =>
                    c._id === data.card._id || c.shortId === data.card.shortId
                  );

                  if (cardExists) return list;

                  // Add card at position
                  const newCards = data.position === 'top'
                    ? [data.card, ...cards]
                    : [...cards, data.card];

                  return { ...list, cards: newCards };
                }
                return list;
              })
            };
          });

          // Update totalCards count for the list
          dispatch(incrementTotalCards({ actionListId: data.actionListId }));
        }
        break;

      case ActionTypes.CARD_UPDATED:
        // ✅ Update card in context
        if (data.card) {
          setBoard((prevBoard) => {
            if (!prevBoard?.actionLists) return prevBoard;

            return {
              ...prevBoard,
              actionLists: prevBoard.actionLists.map((list) => {
                if (!list.cards) return list;

                const cardIndex = list.cards.findIndex(c =>
                  c._id === data.card._id || c.shortId === data.card.shortId ||
                  c._id === data.card.shortId || c.shortId === data.card._id
                );

                if (cardIndex !== -1) {
                  const updatedCards = [...list.cards];
                  updatedCards[cardIndex] = { ...updatedCards[cardIndex], ...data.card };
                  return { ...list, cards: updatedCards };
                }

                return list;
              })
            };
          });
        }
        break;

      case ActionTypes.CARD_DELETED:
        // ✅ Remove card from context
        setBoard((prevBoard) => {
          if (!prevBoard?.actionLists) return prevBoard;

          let cardDeleted = false;
          let deletedFromListId = null;
          const actionLists = prevBoard.actionLists.map((list) => {
            if (!list.cards) return list;

            const initialLength = list.cards.length;
            const filteredCards = list.cards.filter(c =>
              c._id !== data.cardId && c.shortId !== data.cardId
            );

            if (filteredCards.length < initialLength) {
              cardDeleted = true;
              deletedFromListId = list._id || list.shortId;
            }

            return { ...list, cards: filteredCards };
          });

          // Update totalCards count for the list
          if (cardDeleted && deletedFromListId) {
            dispatch(decrementTotalCards({ actionListId: deletedFromListId }));
          }

          return {
            ...prevBoard,
            actionLists,
            cardsCount: cardDeleted ? Math.max(0, (prevBoard.cardsCount || 0) - 1) : prevBoard.cardsCount
          };
        });
        break;

      case ActionTypes.CARD_MOVED:
        // ✅ Move card in context
        if (data.card && data.sourceListId && data.targetListId !== undefined && data.targetPosition !== undefined) {
          setBoard((prevBoard) => {
            if (!prevBoard || !prevBoard.actionLists) return prevBoard;

            const cardId = data.card._id || data.card.shortId;
            let movedCard = null;

            // Remove card from source list
            const listsAfterRemoval = prevBoard.actionLists.map((list) => {
              if (list._id === data.sourceListId || list.shortId === data.sourceListId) {
                const cards = list.cards || [];
                const cardIndex = cards.findIndex(c => c._id === cardId || c.shortId === cardId);

                if (cardIndex !== -1) {
                  movedCard = cards[cardIndex];
                  return { ...list, cards: cards.filter((_, i) => i !== cardIndex) };
                }
              }
              return list;
            });

            // Add card to target list at position
            const finalLists = listsAfterRemoval.map((list) => {
              if (list._id === data.targetListId || list.shortId === data.targetListId) {
                const cards = list.cards || [];
                const newCards = [...cards];
                newCards.splice(data.targetPosition, 0, movedCard || data.card);
                return { ...list, cards: newCards };
              }
              return list;
            });

            return { ...prevBoard, actionLists: finalLists };
          });

          // Update totalCards count for both source and target lists (if different)
          if (data.sourceListId !== data.targetListId) {
            dispatch(updateTotalCardsOnMove({
              sourceListId: data.sourceListId,
              targetListId: data.targetListId
            }));
          }

          // Also update the move card target for UI feedback
          dispatch(setCardMoveTarget({
            targetListId: data.targetListId,
            targetPosition: data.targetPosition,
            card: data.card,
            cardAction: 'MOVE'
          }));
        }
        break;

      case ActionTypes.CARD_ARCHIVED:
        // ✅ Remove archived card from context (only if archived=true)
        if (data.card && data.archived !== false) {
          setBoard((prevBoard) => {
            if (!prevBoard?.actionLists) return prevBoard;

            let cardDeleted = false;
            let archivedFromListId = null;
            const actionLists = prevBoard.actionLists.map((list) => {
              if (!list.cards) return list;

              const initialLength = list.cards.length;
              const filteredCards = list.cards.filter(c =>
                c._id !== (data.card._id || data.card.shortId) &&
                c.shortId !== (data.card._id || data.card.shortId)
              );

              if (filteredCards.length < initialLength) {
                cardDeleted = true;
                archivedFromListId = list._id || list.shortId;
              }

              return { ...list, cards: filteredCards };
            });

            // Update totalCards count for the list
            if (cardDeleted && archivedFromListId) {
              dispatch(decrementTotalCards({ actionListId: archivedFromListId }));
            }

            return {
              ...prevBoard,
              actionLists,
              cardsCount: cardDeleted ? Math.max(0, (prevBoard.cardsCount || 0) - 1) : prevBoard.cardsCount
            };
          });
        }
        break;

      case ActionTypes.CARD_UNARCHIVED:
        // ✅ Add unarchived card back to its list
        if (data.card && data.card.actionList) {
          setBoard((prevBoard) => {
            if (!prevBoard?.actionLists) return prevBoard;

            const actionLists = prevBoard.actionLists.map((list) => {
              // Find the list where the card belongs
              if (list._id === data.card.actionList._id ||
                  list._id === data.card.actionList ||
                  list.shortId === data.card.actionList.shortId ||
                  list.shortId === data.card.actionList) {
                const cards = list.cards || [];

                // Check if card already exists
                const cardExists = cards.some(c =>
                  c._id === data.card._id || c.shortId === data.card.shortId
                );

                if (!cardExists) {
                  // Add card back to the list
                  return { ...list, cards: [...cards, data.card] };
                }
              }
              return list;
            });

            return {
              ...prevBoard,
              actionLists,
              cardsCount: (prevBoard.cardsCount || 0) + 1
            };
          });

          // Update totalCards count for the list
          const targetListId = data.card.actionList._id || data.card.actionList;
          dispatch(incrementTotalCards({ actionListId: targetListId }));
        }
        break;

      case ActionTypes.BOARD_UPDATED:
        // ✅ Update board properties in both Redux and Context
        if (data.board || data.updatedFields) {
          const updates = data.board || data.updatedFields;

          // IMPORTANT: Exclude actionLists from updates to prevent overwriting populated lists with ObjectIds
          const { actionLists, ...safeUpdates } = updates;

          // Update Redux state
          dispatch(optimisticUpdateBoard(safeUpdates));

          // Update Context state (for title, visibility, description, etc.)
          // NOTE: BoardWrapper will handle syncing these updates to context
          // We don't need to update context here to avoid duplicate updates

          console.log('[Board] Board updated:', safeUpdates);
        }
        break;

      case ActionTypes.BOARD_DELETED:
        // Board was deleted - show message
        showError(dispatch, 'This board has been deleted');
        break;

      default:
        console.log('[Board] Unhandled real-time action:', data.action);
    }
  }, [boardId, user?.user?._id, dispatch, setBoard]);




  // Event handlers for form visibility
  const handleShowForm = () => {
    setShowForm(true);
    setTimeout(() => {
      textareaRef.current?.focus();
      formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' }); // Scroll into view
    }, 0);
  };

  const handleHideForm = () => setShowForm(false);
  const handleToggleOptions = (listId) => setOpenListId((prev) => (prev === listId ? null : listId));
  const handleDeleteList = (listId) => setBoard((prev) => ({
    ...prev,
    actionLists: prev.actionLists.filter((list) => list._id !== listId),
  }));

  // Board highlight style applied directly in JSX when needed


  // Recommendation handlers
  const handleViewRecommendations = () => {
    setShowRecommendationSnackbar(false);
    setShowRecommendationModal(true);
  };

  const handleDismissRecommendations = () => {
    setShowRecommendationSnackbar(false);
    if (board?.shortId) {
      recommendationNotificationService.markNotificationDismissed(board.shortId);
    }
  };

  const handleCloseRecommendationModal = () => {
    setShowRecommendationModal(false);
  };

  // Determine current requested board id and whether the in-memory board is from a different board
  const requestedId = boardId || _boardId;
  const isDifferentBoard = Boolean(board?.shortId && requestedId && board.shortId !== requestedId);

  // Loading state handling: show skeleton during fetch or when current board mismatches the requested route
  if (isLoading || boardLoading || isDifferentBoard) {
    return <SkeletonBoard />;
  }

  // Access error handling
  if (accessError) {
    if (accessError.type === 'ACCESS_DENIED') {
      return <AccessDenied message={accessError.message} type="board" />;
    } else if (accessError.type === 'NOT_FOUND') {
      return <NotFound />;
    } else {
      return (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          color: 'var(--primary-text-color)',
          flexDirection: 'column',
          gap: '16px'
        }}>
          <span className="material-symbols-outlined" style={{ fontSize: '48px', opacity: 0.5 }}>error</span>
          <p>{accessError.message}</p>
        </div>
      );
    }
  }

  return (
    <>
      {board?.title && (
        // <Helmet>
        //   <title>{board.title} | Zoobbe</title>
        //   <meta name="description" content="Your dynamic description here" />
        // </Helmet>

        <MetaTags
          title={`${board.title} | ${appName}`}
          description={board?.description}
          ogImage={board?.cover?.url}
          ogUrl={board.url}
        />
      )}

      {board?.wShortId && <PaymentNotice workspaceId={board.wShortId} ref={paymentNoticeRef} />}


      <Navbar
        cardCount={cardCount}
        setCardCount={setCardCount}
        board={board}
        setBoard={setBoard}
        isLoading={isLoading}
        isReadOnly={boardPermissions.isReadOnly}
        ref={navbarRef}
      />

      {/* Read-only mode banner */}
      {boardPermissions.isReadOnly && (
        <div style={{
          backgroundColor: 'var(--warning-bg, #fff3cd)',
          color: 'var(--warning-text, #856404)',
          padding: '8px 16px',
          textAlign: 'center',
          fontSize: '14px',
          borderBottom: '1px solid var(--warning-border, #ffeaa7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '8px',
        }}>
          <span className="material-symbols-outlined" style={{ fontSize: '18px' }}>
            visibility
          </span>
          <span>
            You're viewing this board in read-only mode. {board?.visibility === 'Public' ? 'Only board members can edit.' : 'Request access to make changes.'}
          </span>
        </div>
      )}

      <div
        className={`zoobbe-workspace-board-container${isScrolling ? ' scrolling' : ''}`}
        ref={parentRef}
        style={{
          width: '100%',
          overflowX: 'auto',
          overflowY: 'hidden',
        }}
      >
        <ol
          className='zoobbe-board'
          style={{
            width: virtualizer.getTotalSize(),
            height: `calc(100vh - ${navbarHeight + 75 - 56}px)`,
            position: 'relative',
            margin: 0,
            padding: '0 8px',
            listStyle: 'none',
          }}
          ref={boardRef}
        >
          {virtualizer.getVirtualItems().map((virtualRow) => {
            const index = virtualRow.index;

            // Safely access board properties with fallbacks
            let list = null;
            if (board?.actionLists && index < board.actionLists.length) {
              list = board.actionLists[index];

              // Fetch cards for this list if needed, with debounce to prevent excessive API calls
              if (list && list._id && !fetchedListsRef.current.has(list._id)) {
                fetchedListsRef.current.add(list._id);

                // Use a more robust debounce approach
                const listId = list._id; // Capture in closure
                setTimeout(() => {
                  // Don't fetch if fetching is disabled
                  if (fetchedListsRef.current.has(listId) &&
                    allowFetchingRef.current) {
                    fetchCardsForList(listId);
                  }
                }, 150);
              }
            }

            return (
              <li
                className='zoobbe-action-list'
                key={list?._id || `add-list-${index}`}
                data-index={index}
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: 290,
                  height: '100%',
                  transform: `translateX(${virtualRow.start}px)`,
                  padding: '0 5px',
                }}
                data-listid={list?._id}
              >
                {list ? (
                  <ActionListPND
                    number={index}
                    actionList={list}
                    isOpen={openListId === list._id}
                    onToggleOptions={() => handleToggleOptions(list._id)}
                    onDelete={handleDeleteList}
                    board={{ ...board }}
                    setBoard={setBoard}
                    boardId={board?.shortId}
                    hasLoadedInitialCards={hasLoadedInitialCards}
                    boardScrollRef={parentRef}
                    isReadOnly={boardPermissions.isReadOnly}
                  />
                ) : (
                  // Render the "Add List" button and form at the end
                  <div className="action-list-button-wrapper" ref={formRef}>
                    {showForm ? (
                      <form onSubmit={handleAddList} style={{ width: '100%', padding: '10px' }}>
                        <textarea
                          className="add-new-list"
                          ref={textareaRef}
                          name="add-new-list"
                          placeholder="Enter list title"
                          rows="1"
                          value={listTitle}
                          onChange={(e) => setActionListTitle(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              handleAddList(e);
                            }
                          }}
                          required
                        />
                        <div className="footer-buttons">
                          <button type="submit">Add List</button>
                          <button type="button" onClick={handleHideForm}>
                            <Close size={24} />
                          </button>
                        </div>
                      </form>
                    ) : (
                      <>
                        {!isLoading && !boardPermissions.isReadOnly && (
                          <AddActionListButton onClick={handleShowForm} />
                        )}
                      </>
                    )}
                  </div>
                )}
              </li>
            );
          })}
        </ol>
      </div>

      {/* Card Recommendation Snackbar */}
      {/* <CardRecommendationSnackbar
        boardId={board?.shortId}
        show={showRecommendationSnackbar}
        onViewRecommendations={handleViewRecommendations}
        onDismiss={handleDismissRecommendations}
      /> */}

      {/* Card Recommendation Modal */}
      {showRecommendationModal && (
        <AICardSuggestions
          boardId={board?.shortId}
          onClose={handleCloseRecommendationModal}
        />
      )}

    </>
  );
};

export default Board;
